// SIL-1183: pure step model for the individual-org Add Asset wizard. Kept
// out of routes/assets/submit.tsx (and out of AddAssetStepper.tsx) so both
// the sticky progress bar and the form's inline section numbers (StepTag)
// read off the exact same step definitions and can never drift apart, and
// so SIL-1184 (restyling on top of this state) has a stable, unit-tested
// surface to build against instead of JSX to re-derive.
//
// Two step sets, chosen by the selected add-asset method (see
// wizardStepIdsForMethod below):
//   TrueRead (method 'probe'):            Method -> Build probe -> Run & capture -> Confirm
//   Manual / Scan (method 'manual'|'scan'): Method -> Identify -> Condition & origin -> Confirm
//
// SIL-1195: "Run & capture" is a dwell state, not a one-shot step - the
// probe is reusable across many machines, so a reading landing (even the
// first one) never advances it. It only completes once the user explicitly
// ends the session ("Done capturing"), tracked by `probeSessionDone` below.

export type ProbeStepId = 'method' | 'build' | 'run' | 'confirm';
export type FormStepId = 'method' | 'identify' | 'condition' | 'confirm';
export type WizardStepId = ProbeStepId | FormStepId;

export const PROBE_STEPS: readonly ProbeStepId[] = ['method', 'build', 'run', 'confirm'];
export const FORM_STEPS: readonly FormStepId[] = ['method', 'identify', 'condition', 'confirm'];

const STEP_LABELS: Record<WizardStepId, string> = {
  method: 'Method',
  build: 'Build probe',
  run: 'Run & capture',
  identify: 'Identify',
  condition: 'Condition & origin',
  confirm: 'Confirm',
};

/**
 * Inputs a caller derives from its own form/panel state. Kept as plain
 * booleans (not the raw form shape) so this module never needs to know
 * about `submit.tsx`'s fields.
 */
export interface WizardStepCompletion {
  /** A TrueRead probe was successfully built (ProbeCommandPanel's onProbeBuilt fired). */
  probeBuilt: boolean;
  /**
   * Manual/scan only: a submission posted successfully. Drives FORM_STEPS'
   * `confirm`. SIL-1195: a matched TrueRead reading no longer sets this -
   * the probe is a reusable, many-machine dwell state, not a one-shot
   * capture - see `probeSessionDone` below for the TrueRead equivalent.
   */
  captureSucceeded: boolean;
  /**
   * TrueRead only: the user chose "Done capturing", ending the probe
   * session. Drives PROBE_STEPS' `run` and `confirm` - a reading landing
   * (even the first one) does NOT set this on its own (SIL-1195: no
   * auto-advance on any reading).
   */
  probeSessionDone: boolean;
  /** Make, model, asset class and every serial are filled, with no duplicate serials. */
  identifyComplete: boolean;
  /** A condition card is chosen (not preselected in the wizard - see submit.tsx). */
  conditionSelected: boolean;
}

export interface WizardStep {
  id: WizardStepId;
  label: string;
  /** Done - per the rules in this ticket, everything before `current` is always complete. */
  complete: boolean;
  /** The first incomplete step. Never two steps are current at once; none is current once every step is complete. */
  current: boolean;
}

function isStepComplete(id: WizardStepId, completion: WizardStepCompletion, isProbeFlow: boolean): boolean {
  switch (id) {
    case 'method':
      return true; // a method is always selected - this is the render-0 state
    case 'build':
      return completion.probeBuilt;
    case 'run':
      return completion.probeSessionDone;
    case 'identify':
      return completion.identifyComplete;
    case 'condition':
      return completion.conditionSelected;
    case 'confirm':
      // `confirm` is the one id both step sets share, but the two flags are
      // NOT interchangeable - `isProbeFlow` (derived from which step set the
      // caller passed in, see computeWizardSteps) picks the one that's
      // actually meaningful, so a stray `captureSucceeded` from an unrelated
      // manual/scan flow can never complete PROBE_STEPS' Confirm, and vice
      // versa.
      return isProbeFlow ? completion.probeSessionDone : completion.captureSucceeded;
    default:
      return false;
  }
}

/**
 * current = first step not done; everything before it is complete. When
 * every step is done (a capture just succeeded), the last step stays
 * "current" rather than leaving no step current at all - callers that keep
 * rendering the bar during the success panel (see AddAssetStepper) still
 * have one step to point `aria-current="step"` at.
 */
export function computeWizardSteps(
  stepIds: readonly WizardStepId[],
  completion: WizardStepCompletion,
): WizardStep[] {
  const isProbeFlow = stepIds.includes('build');
  const completeFlags = stepIds.map((id) => isStepComplete(id, completion, isProbeFlow));
  const firstIncomplete = completeFlags.findIndex((done) => !done);
  const currentIndex = firstIncomplete === -1 ? stepIds.length - 1 : firstIncomplete;
  return stepIds.map((id, i) => ({
    id,
    label: STEP_LABELS[id],
    complete: completeFlags[i] ?? false,
    current: i === currentIndex,
  }));
}

/** TrueRead gets PROBE_STEPS; every other method (manual, scan) gets FORM_STEPS. */
export function wizardStepIdsForMethod(method: string): readonly WizardStepId[] {
  return method === 'probe' ? PROBE_STEPS : FORM_STEPS;
}
